import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

/**
 * Pre-auth marketing landing at /. CTAs:
 *   - "Start registration" → /register (5-step wizard)
 *   - "I already have an account" → /signin (only when signed out)
 *   - "Signed in as ..." + sign-out (only when signed in — happens
 *     when Google sign-in completed but firm registration didn't
 *     finish, so we land back here without the app shell)
 *
 * Mirrors step 1 of the registration mockup (feature grid + trial pitch)
 * so the flow is continuous when the user clicks register.
 */
export function MarketingLanding() {
  const { t } = useTranslation(['register', 'common']);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const features = ['clients', 'cases', 'docs', 'calendar', 'billing', 'gmail'] as const;

  return (
    <main className="reg-wrap">
      <header className="reg-brand">
        <div className="reg-brand-name">{t('brand.name')}</div>
        <div className="reg-brand-sub">{t('brand.tagline')}</div>
      </header>

      <section className="reg-panel reg-panel-landing">
        <h1 className="reg-step-title">{t('step_about.title')}</h1>
        <p className="reg-step-sub">{t('step_about.subtitle')}</p>

        <div className="reg-feature-list">
          {features.map((k) => (
            <div className="reg-fi" key={k}>
              <div className="reg-fi-ico" aria-hidden="true">
                ✓
              </div>
              <div>
                <div className="reg-fi-t">{t(`step_about.features.${k}_t`)}</div>
                <div className="reg-fi-s">{t(`step_about.features.${k}_s`)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="reg-info-banner">
          <span aria-hidden="true">ⓘ</span>
          <span>{t('step_about.trial_banner')}</span>
        </div>

        <button
          type="button"
          className="btn-primary reg-cta"
          onClick={() => navigate('/register')}
        >
          {t('step_about.cta')}
        </button>
        {user ? (
          <>
            <div className="reg-signed-in-as">
              {t('step_about.signed_in_as', { email: user.email ?? '' })}
            </div>
            <button
              type="button"
              className="btn-secondary reg-have-account"
              onClick={() => signOut()}
            >
              {t('common:actions.sign_out')}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-secondary reg-have-account"
            onClick={() => navigate('/signin')}
          >
            {t('step_about.have_account')}
          </button>
        )}
      </section>
    </main>
  );
}
